import React from "react";

import { notification, Space } from "antd";
import { useNavigate } from "react-router-dom";

import { BackButton } from "components/BackButton";
import { SubmitButton } from "components/SubmitButton";
import { SummarySection } from "components/SummarySection";
import { HOME_PATH } from "constants/pages";
import { useCountriesContext } from "contexts/countriesCtx";
import { useKycData, useMutateKyc, useStepsProgress, useUser } from "hooks";
import { KycInd } from "types/kyc";
import { getCountryByCode } from "utils/formData";

import { isSanctionedCountryQuestionsRequired } from "../../helpers";

import styles from "./CarriedOverConfirmation.module.scss";

type Props = {
  mode?: "carried-over" | "renewal";
  onDeclineToEdit?: () => void;
};

export const CarriedOverConfirmation = ({ mode = "carried-over", onDeclineToEdit }: Props) => {
  const { data } = useKycData();
  const { data: userData } = useUser();
  const navigate = useNavigate();

  if (!data) return null;

  const currentData = data.last as KycInd;
  const previousData = data.previous as KycInd | null | undefined;
  const { countriesOptions = [] } = useCountriesContext();
  const updateKyc = useMutateKyc();
  const { goToNextScreen, goToPreviousScreen, setScreen } = useStepsProgress();
  const isRenewalConfirmation = mode === "renewal";
  const hasIdentityRejection = Boolean(
    currentData.rejectionReasons?.identity ||
      previousData?.rejectionReasons?.identity ||
      userData?.kyc.rejectionReasons?.identity
  );

  const poaData = [
    { label: "country", value: getCountryByCode(currentData.addressCountry, countriesOptions) },
    { label: "address", value: currentData.addressLine1 },
    { label: "city", value: currentData.city },
    { label: "state", value: currentData.state },
    { label: "postcode/zip", value: currentData.postcode },
    {
      label: "place of birth",
      value: getCountryByCode(currentData.placeOfBirth, countriesOptions),
    },
    {
      label: "nationality",
      value: getCountryByCode(currentData.nationality, countriesOptions),
    },
    { label: "another nationality", value: currentData.secondaryNationalities?.[0].has || "No" },
  ];

  function handleConfirmation(): void {
    if (isRenewalConfirmation) {
      const isSanctionedCountryStepRequired = isSanctionedCountryQuestionsRequired([
        currentData.addressCountry,
        currentData.nationality,
      ]);
      const nextScreen = isSanctionedCountryStepRequired ? 4 : 5;

      updateKyc.mutateAsync({ currentStep: nextScreen }).then(() => {
        setScreen(nextScreen);
      });
      return;
    }

    if (!hasIdentityRejection) {
      updateKyc.mutateAsync({ currentStep: 3, poaCarriedOverDecision: "agree" }).then(() => {
        notification.success({ message: "Address confirmed. Your account is up to date, and no new KYC is required." });
        navigate(HOME_PATH);
      });
      return;
    }

    updateKyc.mutateAsync({ currentStep: 7, poaCarriedOverDecision: "agree" }).then(() => {
      goToNextScreen();
    });
  }

  function handleDecline(): void {
    if (isRenewalConfirmation) {
      onDeclineToEdit?.();
      return;
    }

    updateKyc.mutateAsync({ currentStep: 3, poaCarriedOverDecision: "disagree" }).then(() => {
      setScreen(3);
    });
  }

  return (
    <div className={styles.addressDetails}>
      <h2 data-cy='address-details-title'>Confirm your address details</h2>
      <p>Please confirm if the address we have on file is accurate and up-to-date</p>

      <SummarySection header='' headerButton={""} data={poaData} spaceSize={20} />

      <Space align='center' className={styles.actions} direction='vertical' size={0}>
        <p>Is this address still correct, or do you need to update it?</p>
        <SubmitButton onClick={handleConfirmation} className={styles.carriedOverAccept}>
          Yes, this address is correct
        </SubmitButton>
        <BackButton onClick={handleDecline} className={styles.carriedOverDecline}>
          No, I need to update it
        </BackButton>
        <BackButton
          onClick={() => {
            goToPreviousScreen(() => {
              updateKyc.mutateAsync({ currentStep: 2 });
            });
          }}
        />
      </Space>
    </div>
  );
};
